import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, FileText, CreditCard, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, refreshCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    delivery_address: '',
    delivery_phone: '',
    delivery_notes: ''
  });

  // If returning from a failed payment
  const orderId = searchParams.get('order_id');

  // Redirect to sign in if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      toast.info('Please sign in to complete your purchase');
      navigate('/auth?redirect=/checkout');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderResponse = await axios.post(`${API}/orders`, formData);
      const order = orderResponse.data;

      // Create payment checkout
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
        order_id: order.id,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = paymentResponse.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process checkout');
      setLoading(false);
    }
  };

  const handlePayExistingOrder = async () => {
    if (!orderId) return;
    
    setLoading(true);
    try {
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
        order_id: orderId,
        origin_url: window.location.origin
      });

      window.location.href = paymentResponse.data.checkout_url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setLoading(false);
    }
  };

  // Show payment retry for existing order
  if (orderId) {
    return (
      <div className="min-h-screen flex flex-col bg-stone-50" data-testid="checkout-page">
        <Navbar />
        <main className="flex-1">
          <div className="max-w-lg mx-auto px-4 py-16">
            <div className="bg-white rounded-xl p-8 shadow-sm text-center">
              <h1 className="text-2xl font-bold text-stone-900 mb-4">Complete Your Payment</h1>
              <p className="text-stone-600 mb-6">
                Your order was created but payment was not completed. 
                Click below to retry payment.
              </p>
              <Button
                onClick={handlePayExistingOrder}
                className="btn-primary w-full py-6"
                disabled={loading}
                data-testid="retry-payment-btn"
              >
                {loading ? (
                  <span className="spinner w-5 h-5"></span>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Continue to Payment
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/orders')}
                className="mt-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                View My Orders
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="checkout-page">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/cart')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Cart
          </Button>

          <h1 className="heading-section text-stone-900 mb-8">Checkout</h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Delivery Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-stone-900 mb-6">Delivery Details</h2>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="address">Delivery Address *</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 w-5 h-5 text-stone-400" />
                      <Textarea
                        id="address"
                        placeholder="Enter your full delivery address"
                        value={formData.delivery_address}
                        onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                        className="pl-10 min-h-[100px]"
                        required
                        data-testid="address-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+27 61 530 4587"
                        value={formData.delivery_phone}
                        onChange={(e) => setFormData({ ...formData, delivery_phone: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Delivery Notes (Optional)</Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 w-5 h-5 text-stone-400" />
                      <Textarea
                        id="notes"
                        placeholder="Any special instructions for delivery..."
                        value={formData.delivery_notes}
                        onChange={(e) => setFormData({ ...formData, delivery_notes: e.target.value })}
                        className="pl-10"
                        data-testid="notes-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Info */}
                <div className="mt-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h3 className="font-semibold text-yellow-800 mb-2">Payment Information</h3>
                  <p className="text-sm text-yellow-700">
                    Your payment will be held in a suspension account until your order is fulfilled. 
                    If any items are out of stock, you will receive a full refund for those items.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary py-6 text-lg mt-6"
                  disabled={loading || cart.items.length === 0}
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <span className="spinner w-5 h-5"></span>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="checkout-summary bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-stone-900 mb-4">Order Summary</h2>
                
                <div className="space-y-3 max-h-64 overflow-auto">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-stone-400 text-xs">
                            No Image
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-900 truncate text-sm">{item.name}</p>
                        <p className="text-xs text-stone-500">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold text-green-900">
                          R{(item.selling_price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-200 mt-4 pt-4 space-y-2">
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotal</span>
                    <span>R{cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Delivery</span>
                    <span className="text-green-600">Free</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-stone-900 pt-2 border-t border-stone-200">
                    <span>Total</span>
                    <span data-testid="checkout-total">R{cart.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
